import React, { useState } from 'react';
import { FormControl, FormLabel, FormErrorMessage, Input, Popover, PopoverTrigger, PopoverContent, PopoverBody, Box, Button, Grid, GridItem, Text, VStack, HStack } from '@chakra-ui/react';
import moment from 'moment-jalaali';
import './JalaliDatePicker.css';

/**
 * کامپوننت انتخاب تاریخ شمسی
 * @param {Object} props - props کامپوننت
 * @param {string} props.value - مقدار تاریخ (میلادی یا شمسی)
 * @param {Function} props.onChange - تابع تغییر تاریخ
 * @param {string} props.placeholder - متن placeholder
 * @param {boolean} props.isInvalid - آیا فیلد نامعتبر است
 * @param {string} props.errorMessage - پیام خطا
 * @param {string} props.label - برچسب فیلد
 * @param {boolean} props.required - آیا فیلد الزامی است
 * @param {boolean} props.saveAsJalali - آیا تاریخ را به صورت شمسی ذخیره کنیم
 */
const JalaliDatePicker = ({ 
  value, 
  onChange, 
  selectedDate,
  onDateChange,
  placeholder = "تاریخ را انتخاب کنید",
  isInvalid = false,
  errorMessage = "",
  label = "تاریخ",
  required = false,
  saveAsJalali = false,
  ...props 
}) => {
  // برای سازگاری با props جدید
  const dateValue = selectedDate || value;
  const dateChangeHandler = onDateChange || onChange;
  const [isOpen, setIsOpen] = useState(false);
  
  // تبدیل تاریخ میلادی به شمسی
  const gregorianToJalali = (gregorianDate) => {
    if (!gregorianDate) return null;
    
    try {
      let date;
      if (typeof gregorianDate === 'string') {
        if (gregorianDate.includes('/')) {
          // اگر تاریخ شمسی است
          const parts = gregorianDate.split('/');
          if (parts.length === 3) {
            return {
              year: parseInt(parts[0]),
              month: parseInt(parts[1]),
              day: parseInt(parts[2])
            };
          }
        } else if (gregorianDate.includes('-')) {
          // اگر تاریخ میلادی است
          date = moment(gregorianDate);
        }
      } else if (gregorianDate instanceof Date) {
        date = moment(gregorianDate);
      }

      if (date && date.isValid()) {
        const jalaliString = date.format('jYYYY/jMM/jDD');
        const parts = jalaliString.split('/');
        return {
          year: parseInt(parts[0]),
          month: parseInt(parts[1]),
          day: parseInt(parts[2])
        };
      }
    } catch (error) {
      console.error('Error converting to Jalali:', error);
    }
    return null;
  };

  // تبدیل تاریخ شمسی به میلادی
  const jalaliToGregorian = (jalaliDate) => {
    if (!jalaliDate || !jalaliDate.year || !jalaliDate.month || !jalaliDate.day) {
      return '';
    }

    try {
      const jalaliString = `${jalaliDate.year}/${jalaliDate.month}/${jalaliDate.day}`;
      const gregorianMoment = moment(jalaliString, 'jYYYY/jMM/jDD');
      if (gregorianMoment.isValid()) {
        // تنظیم زمان به نیمه شب برای جلوگیری از مشکلات timezone
        // استفاده از 00:00 به جای 12:00 برای جلوگیری از تغییر روز هفته
        gregorianMoment.hour(0).minute(0).second(0).millisecond(0);
        return gregorianMoment.format('YYYY-MM-DD');
      }
    } catch (error) {
      console.error('Error converting to Gregorian:', error);
    }
    return '';
  };

  // تبدیل تاریخ شمسی به رشته
  const jalaliToString = (jalaliDate) => {
    if (!jalaliDate || !jalaliDate.year || !jalaliDate.month || !jalaliDate.day) {
      return '';
    }
    return `${jalaliDate.year}/${jalaliDate.month.toString().padStart(2, '0')}/${jalaliDate.day.toString().padStart(2, '0')}`;
  };

  // تبدیل تاریخ میلادی فعلی به شمسی
  const todayJalali = moment().format('jYYYY/jMM/jDD').split('/');
  
  // مقدار فعلی تاریخ شمسی
  const selectedDay = dateValue && dateValue.includes('/') 
    ? { 
        year: parseInt(dateValue.split('/')[0]), 
        month: parseInt(dateValue.split('/')[1]), 
        day: parseInt(dateValue.split('/')[2]) 
      }
    : gregorianToJalali(dateValue);
  
  const [currentYear, setCurrentYear] = useState(() => {
    if (selectedDay) return selectedDay.year;
    return parseInt(todayJalali[0]);
  });
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (selectedDay) return selectedDay.month;
    return parseInt(todayJalali[1]);
  });

  // نام‌های ماه‌های شمسی
  const persianMonths = [
    'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
    'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
  ];

  // نام‌های روزهای هفته
  const persianWeekDays = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

  // تولید تقویم ماه جاری
  const generateCalendar = () => {
    const calendar = [];
    
    try {
      // تعداد روزهای ماه شمسی
      const daysInMonth = moment.jDaysInMonth(currentYear, currentMonth - 1);
      
      // محاسبه روز هفته شروع ماه (0 = شنبه)
      const firstDayMoment = moment(`${currentYear}/${currentMonth}/1`, 'jYYYY/jMM/jDD');
      // تبدیل روز هفته میلادی به شمسی (0 = شنبه)
      let firstDayOfWeek = firstDayMoment.day();
      // تبدیل: 0=Sunday -> 0=شنبه, 1=Monday -> 1=یکشنبه, 2=Tuesday -> 2=دوشنبه, ...
      // فقط یک روز جلو می‌بریم
      firstDayOfWeek = (firstDayOfWeek + 1) % 7;
      
      // روزهای خالی ابتدای ماه
      for (let i = 0; i < firstDayOfWeek; i++) {
        calendar.push(null);
      }

      // روزهای ماه
      for (let day = 1; day <= daysInMonth; day++) {
        calendar.push(day);
      }
    } catch (error) {
      console.error('Error generating calendar:', error);
      // در صورت خطا، تقویم ساده ایجاد کنیم
      for (let day = 1; day <= 30; day++) {
        calendar.push(day);
      }
    }

    return calendar;
  };

  // مدیریت انتخاب تاریخ
  const handleDateSelect = (day) => {
    if (typeof dateChangeHandler !== 'function') {
      console.error('dateChangeHandler is not a function:', dateChangeHandler);
      return;
    }

    if (!day) return;

    const selectedDate = {
      year: currentYear,
      month: currentMonth,
      day: day
    };

    try {
      if (saveAsJalali) {
        // ذخیره به صورت شمسی
        const jalaliString = jalaliToString(selectedDate);
        dateChangeHandler(jalaliString);
      } else {
        // تبدیل به میلادی و ذخیره
        const gregorianString = jalaliToGregorian(selectedDate);
        dateChangeHandler(gregorianString);
      }
      setIsOpen(false);
    } catch (error) {
      console.error('Error handling date selection:', error);
    }
  };

  // نمایش تاریخ در input
  const displayValue = selectedDay ? jalaliToString(selectedDay) : '';

  // بررسی اینکه آیا روز انتخاب شده است
  const isSelectedDay = (day) => {
    return selectedDay && 
           selectedDay.year === currentYear && 
           selectedDay.month === currentMonth && 
           selectedDay.day === day;
  };

  // بررسی اینکه آیا روز امروز است
  const isToday = (day) => {
    return parseInt(todayJalali[0]) === currentYear && 
           parseInt(todayJalali[1]) === currentMonth && 
           parseInt(todayJalali[2]) === day;
  };

  const calendar = generateCalendar();

  return (
    <FormControl isInvalid={isInvalid}>
      <FormLabel>
        {label} {required && '*'}
      </FormLabel>
      <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement="bottom-start">
        <PopoverTrigger>
          <Box>
            <Input
              value={displayValue}
              placeholder={placeholder}
              readOnly
              onClick={() => setIsOpen(true)}
              cursor="pointer"
              {...props}
            />
          </Box>
        </PopoverTrigger>
        <PopoverContent p={4} width="320px">
          <PopoverBody>
            <VStack spacing={4}>
              {/* هدر تقویم */}
              <HStack justify="space-between" width="100%">
                <Button
                  size="sm"
                  onClick={() => {
                    if (currentMonth === 1) {
                      setCurrentMonth(12);
                      setCurrentYear(currentYear - 1);
                    } else {
                      setCurrentMonth(currentMonth - 1);
                    }
                  }}
                >
                  ‹
                </Button>
                <Text fontWeight="bold">
                  {persianMonths[currentMonth - 1]} {currentYear}
                </Text>
                <Button
                  size="sm"
                  onClick={() => {
                    if (currentMonth === 12) {
                      setCurrentMonth(1);
                      setCurrentYear(currentYear + 1);
                    } else {
                      setCurrentMonth(currentMonth + 1);
                    }
                  }}
                >
                  ›
                </Button>
              </HStack>

              {/* روزهای هفته */}
              <Grid templateColumns="repeat(7, 1fr)" gap={2} width="100%">
                {persianWeekDays.map((day, index) => (
                  <GridItem key={index} textAlign="center" fontWeight="bold" color="gray.500" fontSize="sm">
                    {day}
                  </GridItem>
                ))}
              </Grid>

              {/* روزهای ماه */}
              <Grid templateColumns="repeat(7, 1fr)" gap={2} width="100%">
                {calendar.map((day, index) => (
                  <GridItem key={index}>
                    {day ? (
                      <Button
                        size="sm"
                        width="28px"
                        height="28px"
                        minWidth="28px"
                        variant={isSelectedDay(day) ? "solid" : "ghost"}
                        colorScheme={isSelectedDay(day) ? "blue" : "gray"}
                        onClick={() => handleDateSelect(day)}
                        border={isToday(day) ? "2px solid" : "none"}
                        borderColor={isToday(day) ? "blue.500" : "transparent"}
                        fontWeight={isToday(day) ? "bold" : "normal"}
                        fontSize="sm"
                      >
                        {day}
                      </Button>
                    ) : (
                      <Box height="28px" />
                    )}
                  </GridItem>
                ))}
              </Grid>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
      {isInvalid && errorMessage && (
        <FormErrorMessage>{errorMessage}</FormErrorMessage>
      )}
    </FormControl>
  );
};

export default JalaliDatePicker; 